import { DEFAULT_LANG } from '@dehaat/kisan-app-bl/constants/common';
import Crop, { CropsWithCategoryProps } from '@dehaat/kisan-app-bl/models/CropV2';
import Image from 'next/image';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import Paragraph, { VARIANT } from '@/components/Paragraph';
import SearchBox from '@/components/SearchBox';
import useDebounce from '@/hooks/useDebounce';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
import CropCategoryAccordion from '../CropCategoryAccordion';
interface Props {
  categorizedCrops: CropsWithCategoryProps[];
  crops: number[];
  onClick: (id: number) => void;
  setCurrentCrumbIndex: Dispatch<SetStateAction<number>>;
}
const ChooseCrop = ({
  categorizedCrops,
  crops,
  onClick,
  setCurrentCrumbIndex
}: Props) => {
  const allCrops = useRef<Crop[]>([]);
  const {
    locale
  } = useRouter();
  const [filteredCrops, setFilteredCrops] = useState<Crop[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const {
    t
  } = useTranslation('askQuestion');
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const handleSearch = useCallback((newSearchQuery: string) => {
    if (newSearchQuery) {
      const regex = new RegExp(newSearchQuery, 'i');
      const newFilteredCrops = allCrops.current.filter(crop => regex.test(crop.name) || regex.test(crop.app_display_name));
      setFilteredCrops(newFilteredCrops);
    } else {
      setFilteredCrops([]);
    }
  }, []);
  const debouncedSearch = useDebounce(handleSearch, 500);
  useEffect(() => {
    if (isSearching) document.body.style.overflowY = 'hidden';
    return () => {
      document.body.style.overflowY = 'auto';
    };
  }, [isSearching]);
  useEffect(() => {
    allCrops.current = categorizedCrops.reduce((crops: Crop[], category) => crops.concat(...category.crops), []);
  }, [categorizedCrops]);
  const selectedCrops = categorizedCrops.reduce((acc: Crop[], category) => {
    const filteredCrops = category.crops.filter(crop => crops.includes(crop.id));
    return [...acc, ...filteredCrops];
  }, []);
  return <>
      <div className={`bg-white p-4 pt-6 ${isSearching ? 'fixed h-screen left-0 top-0 w-full z-50' : ''}`}>
        <SearchBox lang={locale || DEFAULT_LANG} onBackClick={() => setIsSearching(false)} onFocus={() => setIsSearching(true)} onSearch={debouncedSearch} placeholder={t('search_crop_placeholder')} showBackBtn={isSearching} data-sentry-element="SearchBox" data-sentry-source-file="ChooseCrop.tsx" />
        {isSearching && filteredCrops.length > 0 && <section className="px-8 py-4">
            <Paragraph className="text-neutral-50" isHighlighted>
              {t('suggestions')}
            </Paragraph>
            {filteredCrops.map(({
          app_display_name,
          id,
          image_url
        }) => <button className="flex gap-2 items-center mt-4" key={id} onClick={() => {
          onClick(id);
          setIsSearching(false);
        }}>
                <div className="border border-neutral-40 h-10 overflow-hidden relative rounded-full w-10">
                  <Image alt={app_display_name} className="object-cover" fill src={image_url} />
                </div>
                <Paragraph className="text-neutral-70" variant={VARIANT.MEDIUM}>
                  {app_display_name}
                </Paragraph>
              </button>)}
          </section>}
      </div>
      <div className="flex flex-col gap-4 grow p-4 pb-28">
        {categorizedCrops.map(cropCategory => <CropCategoryAccordion cropCategory={cropCategory} crops={crops} key={cropCategory.category} onClick={crop => onClick(crop.id)} />)}
      </div>
      <div className="bg-white bottom-0 fixed m-auto max-w-7xl p-4 w-full">
        <button className={`p-4 rounded-lg text-white w-full ${crops.length > 0 ? 'bg-primary-100' : 'bg-neutral-30'}`} disabled={crops.length === 0} onClick={() => {
        const eventParams = {
          Crop_name: selectedCrops[0]?.app_display_name || ''
        };
        trackCustomEvent(CustomEventTypes.ASK_HELP_CROP_SELECTION_CONFIRMED, JSON.stringify(eventParams));
        setCurrentCrumbIndex(2);
      }}>
          {t('confirm')}
        </button>
      </div>
    </>;
};
export default ChooseCrop;